import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TinyEditor from "./TinyEditor";
import { SquarePen, Trash2, GripVertical } from "lucide-react";
import AuthContext from "../../../auth/AuthContext";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import TopicService from "../../../services/TopicService";
import AlertService from "../../../services/AlertService";
import ConfirmDialogBox from "../../confirmdiolog/ConfirmDiologBox";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";

const SortableSubTopic = ({ sub, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sub.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center justify-between
        p-3 rounded-md
        bg-slate-50 dark:bg-slate-800
        border border-slate-200 dark:border-slate-700
        ${isDragging ? "opacity-70 shadow-lg" : ""}
      `}
    >
      {/* LEFT */}
      <div className="flex items-center gap-3">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab text-slate-400 hover:text-slate-600"
        >
          <GripVertical size={18} />
        </span>

        <span className="text-sm text-slate-700 dark:text-slate-300">
          {sub.name}
        </span>
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-3">
        <button onClick={onEdit}>
          <SquarePen size={16} />
        </button>

        <button onClick={onDelete} className="text-red-600">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const CreateTopic = () => {
  const navigate = useNavigate();
  const { topicId} = useParams();
  const authContext = useContext(AuthContext);
  const isEditMode = Boolean(topicId);

  const [topicName, setTopicName] = useState("");
  const [passingScore, setPassingScore] = useState("");
  const [isAssessment, setIsAssessment] = useState(false);
  const [description, setDescription] = useState("");
  const [noOfQuestions, setNoOfQuestions] = useState("");
  const [duration, setDuration] = useState("");
  const [subTopics, setSubTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topicNameError, setTopicNameError] = useState("");
  const [noOfQuestionsError, setNoOfQuestionsError] = useState("");
  const [durationError, setDurationError] = useState("");

  // Dialog states
  const [showDeleteTopicDialog, setShowDeleteTopicDialog] = useState(false);
  const [showDeleteSubTopicDialog, setShowDeleteSubTopicDialog] = useState(false);
  const [selectedSubTopicId, setSelectedSubTopicId] = useState(null);
  const { setDynamicLabels } = useBreadcrumb();

  useEffect(() => {
    if (isEditMode) fetchTopicData();
  }, [topicId]);



  const fetchTopicData = async () => {
    try {
      setLoading(true);

      const response = await TopicService.GetTopicById(topicId, authContext);

      if (response?.isSuccess && response?.data) {
        const topic = response.data;
        setTopicName(topic.name || "");
        setPassingScore(topic.passingScore || "");
        setIsAssessment(topic.hasAssessment || false);
        setDescription(topic.description || "");
        setNoOfQuestions(topic.noOfQuestions || "");
        setDuration(topic.duration || "");
       setDynamicLabels({
    topicId: topic.id,
    topicTitle: topic.name,
  });
      } else {
        AlertService.error(response?.errorMessage || "Failed to load topic");
      }

      const subRes = await TopicService.GetAllSubTopics(authContext);

      if (subRes?.isSuccess && subRes?.data) {
        setSubTopics(
          subRes.data.filter((s) => s.topicId === topicId && !s.isDeleted),
        );
      }
    } catch (e) {
      console.error("Error fetching topic:", e);
      AlertService.error(
        e?.response?.data?.errorMessage || "Failed to load topic",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (loading) return;
    if (!topicName.trim()) {
      setTopicNameError("Topic name is required");
      return;
    }

    if (isAssessment) {
      let hasAssessmentError = false;

      if (!noOfQuestions) {
        setNoOfQuestionsError("Number of questions is required");
        hasAssessmentError = true;
      } else {
        setNoOfQuestionsError("");
      }

      if (!duration) {
        setDurationError("Duration is required");
        hasAssessmentError = true;
      } else {
        setDurationError("");
      }

      if (hasAssessmentError) return;
    }

    try {
      setLoading(true);

      const payload = {
        id: isEditMode ? topicId : undefined,
        name: topicName,
        description,
        hasAssessment: isAssessment,
        noOfQuestions: isAssessment ? Number(noOfQuestions) : 0,
        duration: isAssessment ? Number(duration) : 0,
        passingScore: Number(passingScore) || 0,
        isDeleted: false,
      };

      const response = await TopicService.SaveTopic(payload, authContext);

      if (response?.isSuccess) {
        AlertService.success(
          isEditMode
            ? "Topic updated successfully"
            : "Topic created successfully",
        );
        navigate(-1);
      } else {
        AlertService.error(response?.errorMessage || "Failed to save topic");
      }
    } catch (e) {
      console.error("Save topic error:", e);
      AlertService.error(
        e?.response?.data?.errorMessage || "Failed to save topic",
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTopic = async () => {
    try {
      setLoading(true);
      const response = await TopicService.DeleteTopic(topicId, authContext);

      if (response?.isSuccess) {
        navigate(-1);
      } else {
        AlertService.error(response?.errorMessage || "Failed to delete topic");
      }
    } catch (e) {
      console.error("Error deleting topic:", e);
      AlertService.error(
        e?.response?.data?.errorMessage || "Failed to delete topic",
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteSubTopic = async () => {
    try {
      setLoading(true);
      const response = await TopicService.DeleteSubTopic(
        selectedSubTopicId,
        authContext,
      );

      if (response?.isSuccess) {
        setSubTopics((p) => p.filter((s) => s.id !== selectedSubTopicId));
        AlertService.success("SubTopic deleted successfully");
        setSelectedSubTopicId(null);
      } else {
        AlertService.error(
          response?.errorMessage || "Failed to delete sub topic",
        );
      }
    } catch (e) {
      console.error("Error deleting sub topic:", e);
      AlertService.error(
        e?.response?.data?.errorMessage || "Failed to delete sub topic",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-slate-900 p-6 rounded-md border border-slate-200 dark:border-slate-700">
        {/* FORM */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 items-center">
          <div>
            <label className="block text-sm mb-1">Topic Name</label>

       <input
  type="text"
  value={topicName}
  onChange={(e) => setTopicName(e.target.value)}
  className={`
    w-full h-12 px-3 rounded border
    bg-white dark:bg-slate-900
    text-slate-800 dark:text-slate-200
    placeholder:text-slate-400 dark:placeholder:text-slate-500
    focus:outline-none focus:ring-1
    ${
      topicNameError
        ? "border-red-500 focus:ring-red-400"
        : "border-slate-300 dark:border-slate-700 focus:ring-blue-400 dark:focus:ring-blue-600"
    }
  `}
/>


            {topicNameError && (
              <p className="mt-1 text-sm text-red-500">{topicNameError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1">Passing Score</label>
            <input
              type="number"
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              className="  w-full h-12 px-3 rounded border
  bg-white dark:bg-slate-900
  border-slate-300 dark:border-slate-700
  text-slate-800 dark:text-slate-200
  placeholder:text-slate-400 dark:placeholder:text-slate-500
  focus:outline-none focus:ring-1
  focus:ring-blue-400 dark:focus:ring-blue-600"
            />
          </div>
          <div>
            <label className="block text-sm mb-1 opacity-0 select-none">
              Assessment
            </label>

            <div className="flex items-center h-12">
              <label className="flex items-center gap-2 h-12 cursor-pointer">
                <input
                  className="w-5 h-5 text-slate-700 dark:text-slate-300"
                  type="checkbox"
                  checked={isAssessment}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsAssessment(checked);

                    if (!checked) {
                      setNoOfQuestions("");
                      setDuration("");
                    }
                  }}
                />
                Assessment
              </label>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm mb-1">Description</label>
          <TinyEditor value={description} onChange={setDescription} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm mb-1">No of Questions</label>

            <input
              type="number"
              value={noOfQuestions}
              onChange={(e) => {
                setNoOfQuestions(e.target.value);
                if (e.target.value) setNoOfQuestionsError("");
              }}
              disabled={!isAssessment}
              className={`
     w-full h-12 px-3 rounded border
  bg-white dark:bg-slate-900
  border-slate-300 dark:border-slate-700
  text-slate-800 dark:text-slate-200
  placeholder:text-slate-400 dark:placeholder:text-slate-500
  focus:outline-none focus:ring-1
  focus:ring-blue-400 dark:focus:ring-blue-600"
        noOfQuestionsError
          ? "border-red-500 focus:ring-red-400"
          : "border-slate-300 focus:ring-blue-400"
      }
      disabled:bg-gray-100 disabled:cursor-not-allowed
    `}
            />

            {noOfQuestionsError && (
              <p className="mt-1 text-sm text-red-500">{noOfQuestionsError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm mb-1">Duration</label>

            <input
              type="number"
              value={duration}
              onChange={(e) => {
                setDuration(e.target.value);
                if (e.target.value) setDurationError("");
              }}
              disabled={!isAssessment}
              className={`
     w-full h-12 px-3 rounded border
  bg-white dark:bg-slate-900
  border-slate-300 dark:border-slate-700
  text-slate-800 dark:text-slate-200
  placeholder:text-slate-400 dark:placeholder:text-slate-500
  focus:outline-none focus:ring-1
  focus:ring-blue-400 dark:focus:ring-blue-600"
      ${
        durationError
          ? "border-red-500 focus:ring-red-400"
          : "border-slate-300 focus:ring-blue-400"
      }
      disabled:bg-gray-100 disabled:cursor-not-allowed
    `}
            />

            {durationError && (
              <p className="mt-1 text-sm text-red-500">{durationError}</p>
            )}
          </div>
        </div>

        {/* SUB TOPICS */}
        {isEditMode && subTopics.length > 0 && (
          <div className="mt-8">
            <h4 className="mb-3 font-medium">Sub Topics</h4>

            <DndContext collisionDetection={closestCenter}>
              <SortableContext
                items={subTopics.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {subTopics.map((sub) => (
                    <SortableSubTopic
                      key={sub.id}
                      sub={sub}
                      onEdit={() =>
                        navigate(
                          `/knowledge-management/topics/${topicId}/${sub.id}/edit-sub-topic`,
                        )
                      }
                      onDelete={() => {
                        setSelectedSubTopicId(sub.id);
                        setShowDeleteSubTopicDialog(true);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* ACTIONS */}
        <div
          className={`flex items-center mt-8 ${
            isEditMode ? "justify-between" : "justify-end"
          }`}
        >
          {isEditMode && (
            <button
              onClick={() => setShowDeleteTopicDialog(true)}
              disabled={loading}
              className="px-6 py-2 border border-red-500 text-red-600 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          )}

          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              disabled={loading}
              className="px-6 py-2 border rounded      text-slate-700 dark:text-slate-200
              border-slate-300 dark:border-slate-600
              hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={loading}
              className="px-8 py-2 rounded text-white bg-[#0D3744] hover:bg-[#0a2a34] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* DELETE TOPIC DIALOG */}
      <ConfirmDialogBox
        visible={showDeleteTopicDialog}
        onHide={() => setShowDeleteTopicDialog(false)}
        onConfirm={() => {
          setShowDeleteTopicDialog(false);
          confirmDeleteTopic();
        }}
        title={
          <>
            Are you sure you want to delete this <strong>topic</strong>? <br />
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* DELETE SUB TOPIC DIALOG */}
      <ConfirmDialogBox
        visible={showDeleteSubTopicDialog}
        onHide={() => {
          setShowDeleteSubTopicDialog(false);
          setSelectedSubTopicId(null);
        }}
        onConfirm={() => {
          setShowDeleteSubTopicDialog(false);
          confirmDeleteSubTopic();
        }}
        title={
          <>
            Are you sure you want to delete this <strong>sub topic</strong>?{" "}
            <br />
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default CreateTopic;
